import React, { useState, useMemo } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  calculateAverage,
  calculateTrend,
  getExercisesByType,
  getEarliestLatestFingers
} from '../../utils/latenessUtils';

// Calculate bidirectional color for lateness heatmap
// Orange for late (positive), purple for early (negative)
const calculateLatenessColor = (value) => {
  if (value == null) return 'rgb(255, 255, 255)'; // white for no data
  
  const absValue = Math.abs(value);
  const intensity = Math.min(absValue / 100, 1); // normalize to 0-1 (100ms max)
  
  if (value > 0) {
    // Late - orange (more intense as value increases)
    // Orange color: rgb(234, 88, 12) or #ea580c
    const r = 255;
    const g = Math.round(255 - (255 - 88) * intensity);
    const b = Math.round(255 - (255 - 12) * intensity);
    return `rgb(${r}, ${g}, ${b})`;
  } else if (value < 0) {
    // Early - purple (more intense as value decreases)
    // Purple color: rgb(147, 51, 234) or #9333ea
    const r = Math.round(255 - (255 - 147) * intensity);
    const g = Math.round(255 - (255 - 51) * intensity);
    const b = 255;
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Perfect - white
    return 'rgb(255, 255, 255)';
  }
};

const LatenessHeatmap = ({ sessions, timeMode, onCellClick }) => {
  const [expandedTypes, setExpandedTypes] = useState(new Set());

  // Helper to format exercise names
  const formatExerciseName = (name) => {
    if (!name) return '';
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Column definitions
  const columns = [
    { id: 'lh', label: 'LH', entity: 'left' },
    { id: 'l1', label: 'L1', entity: 'left_finger_1' },
    { id: 'l2', label: 'L2', entity: 'left_finger_2' },
    { id: 'l3', label: 'L3', entity: 'left_finger_3' },
    { id: 'l4', label: 'L4', entity: 'left_finger_4' },
    { id: 'l5', label: 'L5', entity: 'left_finger_5' },
    { id: 'rh', label: 'RH', entity: 'right' },
    { id: 'r1', label: 'R1', entity: 'right_finger_1' },
    { id: 'r2', label: 'R2', entity: 'right_finger_2' },
    { id: 'r3', label: 'R3', entity: 'right_finger_3' },
    { id: 'r4', label: 'R4', entity: 'right_finger_4' },
    { id: 'r5', label: 'R5', entity: 'right_finger_5' },
  ];

  // Get exercise types and exercises
  const exerciseTypes = useMemo(() => {
    const types = ['scale', 'arpeggio'];
    return types;
  }, []);

  const exercisesByType = useMemo(() => {
    const result = {};
    exerciseTypes.forEach(type => {
      result[type] = getExercisesByType(sessions, type);
    });
    return result;
  }, [sessions, exerciseTypes]);

  // Toggle expansion
  const toggleExpand = (type) => {
    const newExpanded = new Set(expandedTypes);
    if (newExpanded.has(type)) {
      newExpanded.delete(type);
    } else {
      newExpanded.add(type);
    }
    setExpandedTypes(newExpanded);
  };

  // Calculate worst offenders for scales and arpeggios
  const scaleSessions = useMemo(() => sessions.filter(s => s.exercise_type === 'scale'), [sessions]);
  const arpeggioSessions = useMemo(() => sessions.filter(s => s.exercise_type === 'arpeggio'), [sessions]);
  
  const leftFingersScales = useMemo(() => getEarliestLatestFingers(scaleSessions, 'left'), [scaleSessions]);
  const rightFingersScales = useMemo(() => getEarliestLatestFingers(scaleSessions, 'right'), [scaleSessions]);
  
  const leftFingersArpeggios = useMemo(() => getEarliestLatestFingers(arpeggioSessions, 'left'), [arpeggioSessions]);
  const rightFingersArpeggios = useMemo(() => getEarliestLatestFingers(arpeggioSessions, 'right'), [arpeggioSessions]);

  // Calculate cell value (average lateness)
  const calculateCellValue = (entity, exerciseOrType) => {
    const field = `${entity}_lateness_avg`;
    
    // Filter sessions for this exercise or type
    const filteredSessions = sessions.filter(s => {
      return s.exercise_name === exerciseOrType || s.exercise_type === exerciseOrType;
    });

    if (filteredSessions.length === 0) return null;

    if (timeMode === 'trend') {
      const trend = calculateTrend(filteredSessions, field);
      return trend.difference;
    } else {
      return calculateAverage(filteredSessions, field);
    }
  };

  // Render a cell (without numbers)
  const renderCell = (column, exerciseOrType, isExercise = false) => {
    const value = calculateCellValue(column.entity, exerciseOrType);
    const backgroundColor = calculateLatenessColor(value);
    
    return (
      <td
        key={`${column.id}-${exerciseOrType}`}
        className="px-1 py-1 text-center cursor-pointer hover:ring-2 hover:ring-purple-400 transition-all"
        style={{
          backgroundColor,
          width: '32px',
          height: '24px'
        }}
        onClick={() => onCellClick({ 
          entity: column.entity, 
          exercise: isExercise ? exerciseOrType : null,
          exerciseType: !isExercise ? exerciseOrType : sessions.find(s => s.exercise_name === exerciseOrType)?.exercise_type
        })}
        title={value !== null ? `${value.toFixed(1)}ms ${value >= 0 ? 'late' : 'early'}` : 'No data'}
      >
      </td>
    );
  };

  // Render worst offenders table for a specific type
  const renderWorstOffenders = (type, leftFingers, rightFingers) => {
    return (
      <div className="bg-gray-50 p-2 rounded" style={{ width: '250px', minWidth: '250px' }}>
        <div className="text-xs font-semibold text-gray-600 mb-1">
          Worst Offenders ({type === 'scale' ? 'Scales' : 'Arpeggios'})
        </div>
        <table className="w-full text-[10px]">
          <thead>
            <tr className="border-b border-gray-300">
              <th className="text-left py-0.5"></th>
              <th className="text-center py-0.5 font-semibold" style={{ color: '#9333ea' }}>Left</th>
              <th className="text-center py-0.5 font-semibold" style={{ color: '#ea580c' }}>Right</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-0.5 font-semibold text-gray-600">Early</td>
              <td 
                className="text-center py-0.5 cursor-pointer hover:bg-purple-100 transition-colors"
                onClick={() => leftFingers.earliest && leftFingers.earliest.avg < 0 && onCellClick && onCellClick({
                  entity: `left_finger_${leftFingers.earliest.finger}`,
                  exercise: null,
                  exerciseType: type
                })}
              >
                {leftFingers.earliest && leftFingers.earliest.avg < 0 ? (
                  <>
                    <span className="font-bold" style={{ color: '#9333ea' }}>
                      {leftFingers.earliest.finger}
                    </span>
                    <span className="text-gray-500 ml-1 text-[9px]">
                      ({Math.abs(leftFingers.earliest.avg).toFixed(1)}ms)
                    </span>
                  </>
                ) : (
                  <span>-</span>
                )}
              </td>
              <td 
                className="text-center py-0.5 cursor-pointer hover:bg-orange-100 transition-colors"
                onClick={() => rightFingers.earliest && rightFingers.earliest.avg < 0 && onCellClick && onCellClick({
                  entity: `right_finger_${rightFingers.earliest.finger}`,
                  exercise: null,
                  exerciseType: type
                })}
              >
                {rightFingers.earliest && rightFingers.earliest.avg < 0 ? (
                  <>
                    <span className="font-bold" style={{ color: '#ea580c' }}>
                      {rightFingers.earliest.finger}
                    </span>
                    <span className="text-gray-500 ml-1 text-[9px]">
                      ({Math.abs(rightFingers.earliest.avg).toFixed(1)}ms)
                    </span>
                  </>
                ) : (
                  <span>-</span>
                )}
              </td>
            </tr>
            <tr>
              <td className="py-0.5 font-semibold text-gray-600">Late</td>
              <td 
                className="text-center py-0.5 cursor-pointer hover:bg-purple-100 transition-colors"
                onClick={() => leftFingers.latest && leftFingers.latest.avg > 0 && onCellClick && onCellClick({
                  entity: `left_finger_${leftFingers.latest.finger}`,
                  exercise: null,
                  exerciseType: type
                })}
              >
                {leftFingers.latest && leftFingers.latest.avg > 0 ? (
                  <>
                    <span className="font-bold" style={{ color: '#9333ea' }}>
                      {leftFingers.latest.finger}
                    </span>
                    <span className="text-gray-500 ml-1 text-[9px]">
                      ({leftFingers.latest.avg.toFixed(1)}ms)
                    </span>
                  </>
                ) : (
                  <span>-</span>
                )}
              </td>
              <td 
                className="text-center py-0.5 cursor-pointer hover:bg-orange-100 transition-colors"
                onClick={() => rightFingers.latest && rightFingers.latest.avg > 0 && onCellClick && onCellClick({
                  entity: `right_finger_${rightFingers.latest.finger}`,
                  exercise: null,
                  exerciseType: type
                })}
              >
                {rightFingers.latest && rightFingers.latest.avg > 0 ? (
                  <>
                    <span className="font-bold" style={{ color: '#ea580c' }}>
                      {rightFingers.latest.finger}
                    </span>
                    <span className="text-gray-500 ml-1 text-[9px]">
                      ({rightFingers.latest.avg.toFixed(1)}ms)
                    </span>
                  </>
                ) : (
                  <span>-</span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="flex gap-3">
      {/* Left Side: Worst Offenders - Always visible, aligned to top */}
      <div className="flex flex-col gap-3" style={{ width: '250px', minWidth: '250px' }}>
        {exercisesByType['scale'].length > 0 && renderWorstOffenders('scale', leftFingersScales, rightFingersScales)}
        {exercisesByType['arpeggio'].length > 0 && renderWorstOffenders('arpeggio', leftFingersArpeggios, rightFingersArpeggios)}
      </div>

      {/* Right Side: Heatmap Table */}
      <div className="flex-1 flex flex-col bg-white rounded-lg shadow overflow-hidden">
        <div className="p-3 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Lateness by Exercise</h2>
          
          {/* Color Legend - Bidirectional */}
          <div className="flex items-center gap-3 text-xs">
            <span className="text-gray-600 font-semibold">Legend:</span>
            <div className="flex items-center">
              <span className="text-[10px] text-gray-600 mr-2">Early</span>
              <div className="flex items-start">
                {[-100, -75, -50, -25, 0, 25, 50, 75, 100].map((val, idx) => (
                  <div key={idx} className="flex flex-col items-center">
                    <div 
                      className="w-6 h-3 border-r border-gray-300 first:border-l"
                      style={{ 
                        backgroundColor: calculateLatenessColor(val)
                      }}
                    ></div>
                    <span className={`text-[10px] mt-0.5 ${(val === -100 || val === 0 || val === 100) ? 'text-gray-500' : 'text-transparent'}`}>
                      {(val === -100 || val === 0 || val === 100) ? val : '0'}
                    </span>
                  </div>
                ))}
              </div>
              <span className="text-[10px] text-gray-600 ml-2">Late</span>
            </div>
            <span className="text-gray-500 text-[10px]">(ms)</span>
          </div>
        </div>
        
        <div className="overflow-auto max-h-96">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-gray-100 z-10">
              <tr>
                <th className="px-2 py-1 text-left text-[11px] font-semibold text-gray-700 border-b border-gray-300" style={{ width: '120px', minWidth: '120px', maxWidth: '120px' }}>
                  Exercise
                </th>
                {columns.map(col => (
                  <th 
                    key={col.id}
                    className="px-1 py-1 text-center text-[10px] font-semibold text-gray-700 border-b border-gray-300"
                    style={{ width: '32px' }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {exerciseTypes.map(type => (
                <React.Fragment key={type}>
                  {/* Exercise Type Row */}
                  <tr className="bg-gray-50 hover:bg-gray-100 border-b border-gray-200">
                    <td className="px-2 py-1.5 text-[11px] font-semibold text-gray-900" style={{ width: '120px', minWidth: '120px', maxWidth: '120px' }}>
                      <button
                        onClick={() => toggleExpand(type)}
                        className="flex items-center space-x-1 w-full text-left"
                      >
                        {expandedTypes.has(type) ? (
                          <ChevronDownIcon className="h-3 w-3 flex-shrink-0" />
                        ) : (
                          <ChevronRightIcon className="h-3 w-3 flex-shrink-0" />
                        )}
                        <span className="capitalize truncate">{type}s</span>
                      </button>
                    </td>
                    {columns.map(col => renderCell(col, type, false))}
                  </tr>

                  {/* Expanded Exercise Rows */}
                  {expandedTypes.has(type) && exercisesByType[type].map(exercise => (
                    <tr key={exercise} className="hover:bg-gray-50 border-b border-gray-100">
                      <td className="px-2 pl-6 py-1 text-[10px] text-gray-700 truncate" style={{ width: '120px', minWidth: '120px', maxWidth: '120px' }} title={formatExerciseName(exercise)}>
                        {formatExerciseName(exercise)}
                      </td>
                      {columns.map(col => renderCell(col, exercise, true))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LatenessHeatmap;
